/*
  Resource Profile + Form Container Component
*/
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { goBack } from 'connected-react-router';
import moment from 'moment-timezone';

import {
  fetchResource,
  updateResource,
  updateResourcePassword,
} from 'data_system/resource_portal/actions/resourceActions';
import {
  getPartnerTranslations,
  getPartnerIsPhoneRequired,
  getPartnerIsPhotoRequired,
  getPartnerIsBioRequired,
  getResource,
  getResourceUpdating,
  getPartnerI18nList,
} from 'data_system/resource_portal/selectors';

import { getBrand } from 'data_system/token_auth/selectors';

import { translateWithObject } from 'services/translation';

import { ChangePasswordModal } from 'ui_system/token_auth/ChangePasswordModal';
import ResourceForm from './ResourceForm';
import Notice from '../../components/Notice';
import NavigationBar from '../../components/NavigationBar';

const Profile = () => {
  const dispatch = useDispatch();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const resource = useSelector(getResource);
  const partnerTranslations = useSelector(getPartnerTranslations);
  const t = translateWithObject(partnerTranslations);
  const isPhoneRequired = useSelector(getPartnerIsPhoneRequired);
  const isPhotoRequired = useSelector(getPartnerIsPhotoRequired);
  const isBioRequired = useSelector(getPartnerIsBioRequired);
  const resourceUpdating = useSelector(getResourceUpdating);
  const { name, linkColor, buttonBackgroundColor, buttonTextColor } =
    useSelector(getBrand);
  const languages = useSelector(getPartnerI18nList);
  const { resourceUpdateNotice, resourceUpdateNoticeType } = useSelector(
    (state) => state.resource
  );

  useEffect(() => {
    document.title = `${name} - ${t('MY_PROFILE')}`;
    dispatch(fetchResource());
  }, []);

  // Close the Change Password Modal when resource finishes updating
  useEffect(() => {
    if (!resourceUpdating) setPasswordModalOpen(false);
  }, [resourceUpdating]);

  useEffect(() => {
    if (resourceUpdateNotice && resourceUpdateNotice.length > 0)
      window.scrollTo(0, 0);
  }, [resourceUpdateNotice]);

  if (!resource || !resource.attributes) return null;

  const { i18n, isOnlineExperiencesAllowed } = resource.attributes;

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .nullable()
      .trim()
      .required('Please enter a first name'),
    lastName: Yup.string()
      .nullable()
      .trim()
      .required('Please enter a last name'),
    email: Yup.string()
      .nullable()
      .trim()
      .email('Please enter a valid email address'),
    phone: isPhoneRequired
      ? Yup.string()
          .nullable()
          .min(10, 'Please enter a phone number.')
          .required('Please enter a phone number.')
      : Yup.string().nullable().trim(),
    bio: Yup.string().nullable().trim(),
    avatar: isPhotoRequired
      ? Yup.string().when('$avatarUrl', {
          is: null,
          then: (fieldSchema) =>
            fieldSchema
              .min(10, 'Please upload a profile picture')
              .required('Please upload a profile picture'),
        })
      : Yup.string().nullable().trim(),
    avatarProgress: Yup.number().min(100),
    address: Yup.string().nullable().trim(),
    places_address: Yup.string().nullable().trim(),
    place_id: Yup.string().nullable().trim(),
  });

  // Transforms values
  const apiValueTransform = (rawValues) => {
    // Cast validation schema on values
    const values = validationSchema.cast(rawValues);

    // Phone number cleanup
    let { phone } = values;
    if (phone && phone.length > 0) {
      const code = phone.replace(/ .*/, '');
      const phoneWithoutCode = phone.replace(code, '').trim();

      // If phone number is just the country code, wipe it
      if (phoneWithoutCode.length === 0) {
        phone = '';
      }
    }

    // Avatar cleanup
    let avatar = {};
    if (values.avatar && values.avatar.length > 1)
      avatar = { avatar: values.avatar };

    return {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      bio: values.bio,
      address: values.address,
      places_address: values.placesAddress,
      place_id: values.placeId,
      i18n: values.i18n,
      phone,
      ...avatar,
      time_zone: values.timeZone,
    };
  };

  const onSubmitForm = (values, actions) => {
    const apiValues = apiValueTransform(values);
    dispatch(updateResource(apiValues));
    actions.setSubmitting(false);
  };

  const onSubmitPassword = ({
    currentPassword,
    password,
    passwordConfirmation,
  }) => {
    dispatch(
      updateResourcePassword({
        currentPassword,
        password,
        passwordConfirmation,
      })
    );
  };

  const showLanguageSelect = !!languages.find((lang) => lang.includes('fr'));
  let initialTimezone = resource.attributes.timeZone;
  if (!initialTimezone || initialTimezone.length === 0)
    initialTimezone = moment.tz.guess();

  const initialValues = {
    ...resource.attributes,
    avatar: '',
    avatarProgress: 100,
    timeZone: initialTimezone,
  };

  return (
    <div>
      <ChangePasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        onSubmit={onSubmitPassword}
        isSubmitting={resourceUpdating}
      />
      <Formik
        render={(formikProps) => (
          <React.Fragment>
            <NavigationBar
              backText={t('BACK')}
              onBack={() => dispatch(goBack())}
              title={t('MY_PROFILE')}
              actionText={formikProps.dirty ? t('SAVE') : ''}
              onAction={formikProps.dirty ? formikProps.handleSubmit : null}
              i18n={i18n}
            />
            <Notice
              type={resourceUpdateNoticeType}
              text={resourceUpdateNotice}
              show={!!resourceUpdateNotice}
            />
            <ResourceForm
              {...formikProps}
              t={t}
              isPhoneRequired={isPhoneRequired}
              isBioRequired={isBioRequired}
              isPhotoRequired={isPhotoRequired}
              linkColor={linkColor}
              buttonBackgroundColor={buttonBackgroundColor}
              buttonTextColor={buttonTextColor}
              isSubmitting={resourceUpdating}
              onChangePasswordClick={() => setPasswordModalOpen(true)}
              i18n={i18n}
              showLanguageSelect={showLanguageSelect}
              isOnlineExperiencesAllowed={isOnlineExperiencesAllowed}
            />
          </React.Fragment>
        )}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmitForm}
        enableReinitialize
      />
    </div>
  );
};

export default Profile;
